/**
 * WGS84 meetup pins — works when Cloudflare Pages serves static UI only (no /api).
 * Mirrors backend caribbean_attractions_seed + meetup seeds.
 */
import { CARIBBEAN_ISLANDS, ISLAND_BY_CODE } from "./caribbeanIslands";

/** [country, name, category, lat, lng, short, parish|null, featured, baseUsd] */
const PIN_ROWS = [
  ["barbados", "Harrison's Cave", "attraction", 13.1856, -59.5753, "Iconic cave experience", "St. Thomas", true, 65],
  ["barbados", "Oistins Fish Fry", "restaurant", 13.0678, -59.5456, "Legendary fish fry", "Christ Church", true, 45],
  ["barbados", "Carlisle Bay Marine Park", "beach", 13.0796, -59.6115, "Turtles & wrecks", "St. Michael", true, 55],
  ["barbados", "St. Nicholas Abbey", "attraction", 13.2897, -59.5736, "Heritage & rum", "St. Peter", true, 55],
  ["barbados", "Mount Gay Rum Distillery", "tour", 13.1089, -59.62, "Rum heritage tour", "St. Michael", false, 75],
  ["barbados", "Animal Flower Cave", "attraction", 13.3372, -59.6436, "North coast sea caves", "St. Lucy", false, 85],
  ["barbados", "Oistins Fish Fry — Evening Guide", "meetup", 13.066, -59.554, "Friday-night lime & fresh catch", "Christ Church", true, 45],
  ["barbados", "Private Catamaran — South Coast", "meetup", 13.072, -59.615, "Sail & snorkel charter", "St. Michael", true, 220],
  ["barbados", "Bathsheba Surf — Soup Bowl", "meetup", 13.327, -59.543, "East coast surf lesson", "St. Joseph", false, 85],
  ["jamaica", "Dunn's River Falls", "attraction", 18.4054, -77.1389, "Famous waterfall climb", null, true, 75],
  ["jamaica", "Seven Mile Beach", "beach", 18.3358, -78.341, "Iconic Negril beach", null, true, 40],
  ["jamaica", "Rick's Café", "bar", 18.3502, -78.2133, "Cliff sunset bar", null, true, 50],
  ["jamaica", "Bob Marley Museum", "attraction", 18.0196, -76.7797, "Reggae heritage", null, true, 35],
  ["trinidad", "Maracas Bay", "beach", 10.761, -61.43, "Bake & shark beach", null, true, 30],
  ["trinidad", "Queen's Park Savannah", "attraction", 10.665, -61.515, "Carnival central", null, true, 20],
  ["trinidad", "Caroni Bird Sanctuary", "tour", 10.5833, -61.4333, "Scarlet ibis tour", null, false, 65],
  ["bahamas", "Pink Sands Beach", "beach", 25.5, -76.75, "Pink sand paradise", null, true, 50],
  ["bahamas", "Exuma Pig Beach", "attraction", 24.1833, -76.45, "Swimming pigs", null, true, 120],
  ["bahamas", "Atlantis Paradise Island", "hotel", 25.085, -77.325, "Iconic mega resort", null, true, 150],
  ["stlucia", "Pitons Management Area", "attraction", 13.812, -61.07, "UNESCO Pitons", null, true, 80],
  ["stlucia", "Reduit Beach", "beach", 14.072, -60.949, "Rodney Bay beach", null, true, 35],
  ["antigua", "Nelson's Dockyard", "attraction", 17.032, -61.764, "UNESCO dockyard", null, true, 40],
  ["antigua", "Shirley Heights", "attraction", 17.003, -61.762, "Sunset lookout", null, true, 45],
  ["grenada", "Grand Anse Beach", "beach", 12.02, -61.77, "Flagship beach", null, true, 30],
  ["grenada", "Underwater Sculpture Park", "attraction", 12.08, -61.75, "Snorkel art park", null, true, 70],
  ["stvincent", "La Soufrière Volcano", "adventure", 13.337, -61.161, "Volcano hike", null, true, 95],
  ["stvincent", "Tobago Cays", "beach", 12.633, -61.358, "Sailing paradise", null, true, 110],
  ["dominica", "Trafalgar Falls", "attraction", 15.337, -61.35, "Twin waterfalls", null, true, 45],
  ["dominica", "Champagne Reef", "beach", 15.245, -61.371, "Volcanic snorkel bubbles", null, true, 55],
  ["curacao", "Willemstad Handelskade", "attraction", 12.108, -68.933, "Colorful waterfront", null, true, 25],
  ["curacao", "Mambo Beach", "beach", 12.088, -68.9, "Beach boulevard", null, true, 35],
  ["aruba", "Eagle Beach", "beach", 12.55, -70.05, "Top ranked beach", null, true, 30],
  ["aruba", "Natural Pool (Conchi)", "attraction", 12.5, -69.933, "Hidden natural pool", null, true, 90],
  ["cayman", "Seven Mile Beach", "beach", 19.343, -81.378, "Signature beach", null, true, 40],
  ["cayman", "Stingray City", "tour", 19.376, -81.292, "Stingray sandbar", null, true, 85],
  ["usvi", "Trunk Bay", "beach", 18.35, -64.766, "Snorkel trail beach", null, true, 35],
  ["usvi", "Magen's Bay", "beach", 18.36, -64.933, "Heart-shaped bay", null, true, 25],
  ["bvi", "The Baths", "attraction", 18.43, -64.45, "Boulder grottos", null, true, 50],
  ["bvi", "White Bay", "beach", 18.395, -64.7, "Beach bar paradise", null, true, 45],
  ["puerto_rico", "El Yunque National Forest", "adventure", 18.295, -65.792, "Rainforest hikes", null, true, 60],
  ["puerto_rico", "Old San Juan", "attraction", 18.466, -66.118, "Historic walled city", null, true, 30],
  ["puerto_rico", "Flamenco Beach", "beach", 18.327, -65.316, "Top ranked beach", null, true, 40],
];

function slugify(country, name) {
  return `${country}_${String(name).toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40)}`;
}

function rowToPin(row) {
  const [country, name, category, lat, lng, short, parish, featured, baseUsd] = row;
  const island = ISLAND_BY_CODE[country];
  const pinId = `fallback-${slugify(country, name)}`;
  return {
    pin_id: pinId,
    name,
    description: short,
    country,
    island: island?.name || country,
    parish: parish || null,
    lat,
    lng,
    kind: category === "meetup" ? "meetup" : "spot",
    category,
    tier: featured ? "FEATURED" : "STANDARD",
    base_price_usd: baseUsd,
    is_featured: Boolean(featured),
    is_verified: true,
    locked: false,
    google_maps_url: `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
    source: "fallback",
  };
}

const ALL_PINS = PIN_ROWS.map(rowToPin);

export function getMeetupMapFallback({ country, parish, category } = {}) {
  let pins = ALL_PINS;
  if (country) {
    pins = pins.filter((p) => p.country === country);
  }
  if (parish) {
    const pNorm = parish.trim().toLowerCase();
    pins = pins.filter((p) => (p.parish || "").toLowerCase() === pNorm);
  }
  if (category) {
    const cNorm = category.trim().toLowerCase();
    pins = pins.filter((p) => (p.category || "").toLowerCase() === cNorm);
  }

  const parishes = [...new Set(pins.map((p) => p.parish).filter(Boolean))].sort();
  const islandMeta = country ? ISLAND_BY_CODE[country] : null;

  return {
    pins,
    total: pins.length,
    parishes,
    island_center: islandMeta?.center || { lat: 15.5, lng: -70.0 },
    island_zoom: islandMeta?.zoom || (country ? 11 : 5),
    islands: CARIBBEAN_ISLANDS,
    stats: {
      total_pins: ALL_PINS.length,
      islands: CARIBBEAN_ISLANDS.length,
      featured: pins.filter((p) => p.is_featured).length,
      partners: 0,
    },
    source: "fallback",
  };
}
